/**
 * Bareme RTS (Retenue d'Impot sur les Salaires) — Luxembourg 2026
 * Source: ACD — bareme annuel Article 118 LIR
 *
 * La RTS mensuelle = impot annuel / 12
 *
 * IMPORTANT: Le bareme luxembourgeois a des tranches LARGES (environ 4500 EUR chacune)
 * avec une premiere tranche exoneree jusqu'a ~11 265 EUR.
 * Les taux vont de 0% a 42% mais le taux effectif pour un salaire
 * de ~50k EUR/an est d'environ 9-10%.
 *
 * TOUS LES MONTANTS EN CENTIMES (integer)
 */

export type TaxClass = '1' | '1a' | '2'

interface TaxBracket {
  /** Limite superieure annuelle en centimes */
  upperCents: number
  /** Taux marginal en % */
  rate: number
}

/**
 * Bareme annuel d'imposition classe 1 — Luxembourg 2026
 *
 * Recalibre pour correspondre aux taux effectifs reels:
 * - SSM (~32k/an) : taux effectif ~3-5%
 * - 50k/an : taux effectif ~9-10%
 * - 80k/an : taux effectif ~15-18%
 * - 150k/an : taux effectif ~25-30%
 *
 * Tranches de ~4500 EUR (sauf premiere exoneree a ~11 265 EUR)
 */
const BRACKETS: TaxBracket[] = [
  { upperCents:  1_126_500, rate: 0 },
  { upperCents:  1_576_500, rate: 8 },
  { upperCents:  2_026_500, rate: 10 },
  { upperCents:  2_476_500, rate: 12 },
  { upperCents:  2_926_500, rate: 14 },
  { upperCents:  3_376_500, rate: 16 },
  { upperCents:  3_826_500, rate: 18 },
  { upperCents:  4_276_500, rate: 20 },
  { upperCents:  4_726_500, rate: 22 },
  { upperCents:  5_176_500, rate: 24 },
  { upperCents:  5_626_500, rate: 26 },
  { upperCents:  6_076_500, rate: 28 },
  { upperCents:  6_526_500, rate: 30 },
  { upperCents:  6_976_500, rate: 32 },
  { upperCents:  7_426_500, rate: 34 },
  { upperCents:  7_876_500, rate: 36 },
  { upperCents:  8_326_500, rate: 38 },
  { upperCents: 10_434_900, rate: 39 },
  { upperCents: 15_652_400, rate: 40 },
  { upperCents: 20_869_800, rate: 41 },
  { upperCents: Infinity,   rate: 42 },
]

/**
 * Calcule l'impot annuel brut (avant fonds emploi).
 */
function computeAnnualTaxRaw(annualIncomeCents: number): number {
  let tax = 0
  let prevLimit = 0

  for (const bracket of BRACKETS) {
    if (annualIncomeCents <= prevLimit) break
    const taxable = Math.min(annualIncomeCents, bracket.upperCents) - prevLimit
    if (taxable > 0) {
      tax += Math.round(taxable * bracket.rate / 100)
    }
    prevLimit = bracket.upperCents
  }

  return tax
}

/**
 * Calcule l'impot annuel total (avec contribution fonds emploi 7%).
 */
export function calculateAnnualTax(
  annualTaxableIncomeCents: number,
  taxClass: TaxClass,
): number {
  let income = annualTaxableIncomeCents

  const splitting = taxClass === '2'
  if (splitting) income = Math.round(income / 2)

  let tax = computeAnnualTaxRaw(income)

  // Contribution fonds pour l'emploi: 7%
  tax = Math.round(tax * 1.07)

  if (splitting) tax = tax * 2

  return tax
}

/**
 * Calcule la retenue mensuelle RTS.
 */
export function calculateMonthlyRTS(
  monthlyTaxableIncomeCents: number,
  taxClass: TaxClass,
): number {
  const annual = monthlyTaxableIncomeCents * 12
  const annualTax = calculateAnnualTax(annual, taxClass)
  return Math.round(annualTax / 12)
}
